import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaMinus } from 'react-icons/fa';
import '../Finance/Finance.css';
import financeHero from '../../../assets/Services/Emerging.png';
import CustomButton from '../../Button/CustomButton';

const EmergingDetail = () => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  const navigate = useNavigate();

  const faqData = [
    {
      q: "What types of emerging energy projects does Dazbog support?",
      a: " We support BESS, hybrid renewable projects, green hydrogen, and other new clean energy initiatives."
    },
    {
      q: "2. Do you support early-stage project ideas?",
      a: " Yes. We work with clients from concept and planning stages and help shape ideas into viable projects."
    },
    {
      q: "3. Do you provide support beyond advisory?",
      a: "Yes. We stay involved through planning, coordination, and execution support where required."
    },
    {
      q: "4. Who typically works with Dazbog for emerging energy projects?",
      a: "We work with developers, investors, EPCs, institutions, and government bodies."
    }
  ];

  // Animation variants for sections
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  return (
    <div className="service-detail-page">
      {/* --- HERO SECTION --- */}
      <section className="detail-hero">
        <div className="container">
          <div className="detail-hero-flex">
            <motion.div
              className="detail-hero-content"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Emerging Energy & Strategic Advisory</h1>
              <p>Clean energy is evolving quickly. New technologies like BESS, hybrid systems, and green hydrogen are creating fresh opportunities — but they also bring new questions.</p>
              <p>We help companies understand these opportunities clearly and plan the right way forward.</p>
              <CustomButton
                variant="white"
                size="lg"
                text=" Discuss Your Project"
                onClick={() => navigate("/contact")}
              />
            </motion.div>
            <motion.div
              className="detail-hero-image"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
            >
              <img src={financeHero} alt="Finance Advisory" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- HOW WE SUPPORT SECTION --- */}
      <section className="support-info-section">
        <div className="container">
          <motion.div
            className="support-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <h2>Preparing for the Next Phase of Clean Energy</h2>
            <p >Emerging energy projects require more than just ambition. They need practical planning, technical clarity, and a realistic roadmap.</p>
            <p >We work with developers, investors, and institutions who are exploring new clean energy areas. Our role is to turn early-stage ideas into structured, investment-ready projects.</p>
            <p className="support-lead">We focus on reducing uncertainty and helping teams move forward with confidence.</p>
          </motion.div>

          <div className="services-list-container">
            <motion.h3
              className="list-main-title"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              Emerging Energy & Strategic Advisory
            </motion.h3>

            <motion.div
              className="services-breakdown-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {/* Service Blocks with stagger reveal */}
              {[
                {
                  title: "1. How We Support Your Emerging Energy Project",
                  text1: "When entering a new energy segment, clarity is key.",
                  text2: "We support projects from early concept stage through initial structuring. Our aim is to help teams understand risks, opportunities, and practical next steps.",
                  listTitle: "Our support may include:",
                  list: ["Strategic guidance for new clean energy technologies", "Technical and commercial input during early planning", "Support in understanding policy, market trends, and risks", "Reviewing project positioning before approaching investors"],
                  outro: "We help shape direction before major investments are made."
                },
                {
                  title: "2. Our Emerging Energy Advisory Services",
                  text1: "Each emerging energy project has its own challenges.",
                  text2: "We provide focused support in areas such as:",
                  listTitle: "",
                  list: ["Battery Energy Storage Systems (BESS)", "Hybrid renewable energy projects", "Green hydrogen initiatives", "Early-stage risk assessment and feasibility review"],
                  outro: "Our goal is to make new technologies more practical and investment-ready."
                },
                {
                  title: "3. Investor & Lender Engagement",
                  text1: "Emerging projects often need clear communication with investors and lenders.",
                  text2: "We support discussions throughout the development process by helping present the project clearly and realistically.",
                  listTitle: "This may include:",
                  list: ["Preparing investor presentations", "Coordinating lender discussions", "Supporting during evaluation and approval stages"],
                  outro: "Clear communication builds trust."
                },
                {
                  title: "4. Transaction Closure & Ongoing Support",
                  text1: "Even after initial structuring, projects continue to evolve.",
                  text2: "We stay involved through financial close and early execution milestones. Our aim is to provide stability during critical stages.",
                  listTitle: "This can include:",
                  list: ["Supporting financial close coordination", "Post-close advisory guidance", "Support during refinancing or restructuring"],
                  outro: "We believe long-term support adds real value."
                }
              ].map((service, index) => (
                <motion.div key={index} className="service-text-block" variants={fadeInUp}>
                  <h4>{service.title}</h4>
                  <p>{service.text1}</p>
                  <p>{service.text2}</p>
                  {service.listTitle && <p>{service.listTitle}</p>}
                  <ul>
                    {service.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>{service.outro}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="projects-tags-box"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3>Projects We Work On</h3>
            <div className="tag-flex">
              {[
                { name: "Solar IPP", path: "/projects/solar-ipp" },
                { name: "Solar EPC Project", path: "/projects/solar-epc" },
                { name: "Solar Hybrid EPC Project", path: "/projects/solar-hybrid" },
                { name: "Solar + BESS EPC Project", path: "/projects/solar-bess" }
              ].map((tag, i) => (
                <span
                  key={i}
                  className="project-tag-link"
                  onClick={() => navigate(tag.path)}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="faq-detail-section">
        <div className="container">
          <div className="faq-flex-row">
            <motion.div
              className="faq-left-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2>Emerging Energy Advisory – FAQs</h2>
              <p>Answers to common questions about our role in renewable energy projects.</p>
            </motion.div>
            <div className="faq-right-accordion">
              {faqData.map((faq, i) => (
                <motion.div
                  key={i}
                  className="faq-detail-item"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <div className="faq-detail-q" onClick={() => setActiveFAQ(activeFAQ === i ? null : i)}>
                    <span>{faq.q}</span>
                    {activeFAQ === i ? <FaMinus className="faq-icon" /> : <FaPlus className="faq-icon" />}
                  </div>
                  <AnimatePresence>
                    {activeFAQ === i && (
                      <motion.div
                        className="faq-detail-a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmergingDetail;